import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Button, Pagination, Spin, message, Input, Select, Switch, Typography } from 'antd';
import {
  DatabaseOutlined,
  CloseOutlined,
  FilterOutlined,
  PlusOutlined,
  TableOutlined,
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import { ResponsivePie } from '@nivo/pie';
import { TreeDataGrid } from "react-data-grid";
import 'react-data-grid/lib/styles.css';
import dayjs from 'dayjs';
import ApiService from '../../../src/Components/apiService';
import { BASE_URL } from '../../../src/Components/Constant';
import FiltersDynamic from '../../Components/FiltersDynamicComponent';
import DynamicPicklistComponent from '../../Components/MultiSelectPicklistComponent';
import { FilterValueService } from 'ag-grid-community';


const { Title } = Typography;
// Sample data for chart previews
const sampleBarData = [
  { category: 'A', value: 25 },
  { category: 'B', value: 45 },
  { category: 'C', value: 35 }
];

const sampleLineData = [
  {
    id: 'line',
    data: [
      { x: 'Jan', y: 20 },
      { x: 'Feb', y: 35 },
      { x: 'Mar', y: 25 },
      { x: 'Apr', y: 45 }
    ]
  }
];

const samplePieData = [
  { id: 'A', value: 30, color: '#1890ff' },
  { id: 'B', value: 35, color: '#52c41a' },
  { id: 'C', value: 35, color: '#fa8c16' }
];

// Mini chart components for previews
const MiniBarChart = () => (
  <div style={{ height: '40px', width: '60px' }}>
    <ResponsiveBar
      data={sampleBarData}
      keys={['value']}
      indexBy="category"
      margin={{ top: 5, right: 5, bottom: 15, left: 5 }}
      padding={0.3}
      colors={['#1890ff']}
      borderRadius={2}
      axisTop={null}
      axisRight={null}
      axisBottom={null}
      axisLeft={null}
      enableGridY={false}
      enableLabel={false}
      isInteractive={false}
      animate={false}
    />
  </div>
);

const MiniLineChart = () => (
  <div style={{ height: '40px', width: '60px' }}>
    <ResponsiveLine
      data={sampleLineData}
      margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
      xScale={{ type: 'point' }}
      yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
      curve="monotoneX"
      axisTop={null}
      axisRight={null}
      axisBottom={null}
      axisLeft={null}
      enableGridX={false}
      enableGridY={false}
      colors={['#1890ff']}
      pointSize={3}
      pointBorderWidth={1}
      pointBorderColor="#ffffff"
      enableArea={false}
      isInteractive={false}
      animate={false}
    />
  </div>
);

const MiniPieChart = () => (
  <div style={{ height: '40px', width: '40px' }}>
    <ResponsivePie
      data={samplePieData}
      margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
      innerRadius={0.4}
      colors={['#1890ff', '#52c41a', '#fa8c16']}
      borderWidth={1}
      borderColor="#ffffff"
      enableArcLinkLabels={false}
      enableArcLabels={false}
      isInteractive={false}
      animate={false}
    />
  </div>
);

const MiniTableIcon = () => (
  <div style={{
    height: '40px',
    width: '50px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    justifyContent: 'center'
  }}>
    {[1, 2, 3].map(i => (
      <div key={i} style={{
        height: '8px',
        backgroundColor: '#1890ff',
        borderRadius: '1px',
        opacity: i === 1 ? 1 : 0.6
      }} />
    ))}
  </div>
);

// Global state for data sources - fetch once and reuse
let globalObjects = [];
let isGlobalLoading = false;
let hasGlobalLoadedOnce = false;

// Global state for fields - cache fields per object
let globalFieldsCache = {}; // { objectName: { fields, dimensionFields, measureFields } }
let globalFieldsLoading = {}; // { objectName: boolean }

// Global fetch function for objects
const fetchObjectsGlobally = async (setLoading, setAllObjects) => {
  if (hasGlobalLoadedOnce || isGlobalLoading) {
    setLoading(false);
    setAllObjects(globalObjects);
    return;
  }

  try {
    isGlobalLoading = true;
    setLoading(true);

    const apiService = new ApiService(
      `${BASE_URL}/mt_objects`,
      { 'Content-Type': 'application/json' },
      'GET',
    );

    const response = await apiService.makeCall();

    // Transform API response to match expected format
    const transformedObjects = response.map(obj => ({
      id: obj.api_name || obj.name || obj.id,
      title: obj.display_name || obj.name || obj.title,
      subTitle: obj.category || 'Data',
      description: obj.description || `${obj.display_name || obj.name} data model`,
      fields: obj.field_count || 0
    }));

    globalObjects = transformedObjects;
    hasGlobalLoadedOnce = true;
    setAllObjects(transformedObjects);
  } catch (error) {
    console.error("Failed to fetch objects:", error);
    message.error("Failed to fetch objects");
    globalObjects = [];
  } finally {
    isGlobalLoading = false;
    setLoading(false);
  }
};

// Global fetch function for fields
const fetchFieldsGlobally = async (objectName, setFields, setDimensionFields, setMeasureFields, setLoading) => {
  // Return cached data if available
  if (globalFieldsCache[objectName]) {
    const cached = globalFieldsCache[objectName];
    if (setFields) setFields(cached.fields);
    setDimensionFields(cached.dimensionFields);
    setMeasureFields(cached.measureFields);
    setLoading(false);
    return;
  }

  // Prevent multiple simultaneous requests for the same object
  if (globalFieldsLoading[objectName]) {
    return;
  }

  try {
    globalFieldsLoading[objectName] = true;
    setLoading(true);

    const apiService = new ApiService(
      `${BASE_URL}/mt_fields/object/${objectName}`,
      { 'Content-Type': 'application/json' },
      'GET',
    );

    const response = await apiService.makeCall();

    // Separate measure fields (numeric fields that can be aggregated)
    const measure = response.filter(field =>
      (field.type === 'integer' || field.type === 'decimal' || field.type === 'currency' || field.type === 'rollup') && field.name !== 'recordCount'
    );

    // Dimension fields are all fields EXCEPT measure fields
    const dimensions = response.filter(field =>
      !(field.type === 'integer' || field.type === 'decimal' || field.type === 'currency' || field.type === 'rollup') && field.name !== 'recordCount'
    );

    const filteredFields = response.filter(field => field.name !== 'recordCount'); // Filter out 'recordCount'

    const dimensionFields = [...dimensions, { name: "created_at", label: "Created At" }];
    const measureFields = measure;

    // Cache the results
    globalFieldsCache[objectName] = {
      fields: filteredFields,
      dimensionFields: dimensionFields,
      measureFields: measureFields
    };

    if (setFields) setFields(filteredFields);
    setDimensionFields(dimensionFields);
    setMeasureFields(measureFields);
  } catch (error) {
    console.error("Failed to fetch fields:", error);
    message.error("Failed to fetch fields.");
  } finally {
    globalFieldsLoading[objectName] = false;
    setLoading(false);
  }
};

// --- STEP 1: Data Sources ---
const TableCard = ({ title, subTitle, description, fields, isSelected, onSelect, onRemove }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={`table-card ${isSelected ? 'selected' : ''}`}
      onClick={onSelect}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        border: isSelected ? '2px solid #1890ff' : isHovered ? '1px solid #1890ff' : '1px solid #e8e8e8',
        borderRadius: '8px',
        padding: '16px',
        cursor: 'pointer',
        backgroundColor: isSelected ? '#eefaffff' : isHovered ? '#f0f9ff' : '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '12px',
        marginBottom: '16px',
        transition: 'all 0.3s ease',
        boxShadow: isHovered && !isSelected ? '0 4px 12px rgba(0, 0, 0, 0.1)' : isSelected ? '0 2px 8px rgba(24, 144, 255, 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.05)',
        transform: isHovered && !isSelected ? 'translateY(-2px)' : 'translateY(0)',
        height: '100%',
        width: '100%',
        minHeight: '120px',
        maxWidth: '100%',
        flex: '1'
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        width: '100%'
      }}>
        <DatabaseOutlined style={{
          fontSize: '20px',
          color: isHovered || isSelected ? '#1890ff' : '#8c8c8c',
          marginTop: '2px',
          transition: 'color 0.3s ease',
          flexShrink: 0
        }} />
        <div style={{ flex: 1, minHeight: 0, width: 0 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '4px',
            flexWrap: 'wrap'
          }}>
            <span style={{
              fontSize: '16px',
              fontWeight: '600',
              color: isHovered || isSelected ? '#1890ff' : '#262626',
              transition: 'color 0.3s ease',
              wordBreak: 'break-word'
            }}>
              {title}
            </span>
            {subTitle && (
              <span style={{
                fontSize: '12px',
                padding: '2px 8px',
                backgroundColor: isHovered ? '#e6f4ff' : '#f0f0f0',
                color: isHovered ? '#1890ff' : '#666',
                borderRadius: '4px',
                fontWeight: '500',
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap'
              }}>
                {subTitle}
              </span>
            )}
            {isSelected && (
              <span style={{
                fontSize: '12px',
                padding: '2px 8px',
                backgroundColor: '#1890ff',
                color: '#ffffff',
                borderRadius: '4px',
                fontWeight: '500',
                whiteSpace: 'nowrap'
              }}>
                Primary
              </span>
            )}
          </div>
          <p style={{
            fontSize: '14px',
            color: '#595959',
            margin: '0 0 8px 0',
            lineHeight: '1.4',
            flex: 1,
            wordBreak: 'break-word',
            overflow: 'hidden'
          }}>
            {description}
          </p>
          <p style={{
            fontSize: '13px',
            color: '#8c8c8c',
            margin: '0',
            whiteSpace: 'nowrap'
          }}>
            {fields} fields available
          </p>
        </div>
        {isSelected && (
          <CloseOutlined
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            style={{
              fontSize: '14px',
              color: '#8c8c8c',
              cursor: 'pointer',
              padding: '4px',
              transition: 'color 0.3s ease',
              flexShrink: 0
            }}
            onMouseEnter={(e) => e.target.style.color = '#ff4d4f'}
            onMouseLeave={(e) => e.target.style.color = '#8c8c8c'}
          />
        )}
      </div>
    </div>
  );
};

const Step1Content = ({ selectedTables, onSelectTable, onRemoveTable }) => {
  const [allObjects, setAllObjects] = useState(globalObjects);
  const [loading, setLoading] = useState(!hasGlobalLoadedOnce);
  const [searchText, setSearchText] = useState('');

  // Fetch objects when component mounts - only if not already loaded
  useEffect(() => {
    fetchObjectsGlobally(setLoading, setAllObjects);
  }, []);

  // Filter objects based on search text
  const filteredObjects = useMemo(() => {
    if (!searchText.trim()) return allObjects;

    const searchLower = searchText.toLowerCase().trim();
    return allObjects.filter(obj =>
      obj.title?.toLowerCase().includes(searchLower) ||
      obj.description?.toLowerCase().includes(searchLower) ||
      obj.subTitle?.toLowerCase().includes(searchLower) ||
      obj.id?.toLowerCase().includes(searchLower)
    );
  }, [allObjects, searchText]);

  // Use filtered objects
  const allTables = filteredObjects;

  const selectedTableObjects = allTables.filter(table => selectedTables.includes(table.id));
  const availableTables = allTables.filter(table => !selectedTables.includes(table.id));
  const [page, setPage] = useState(1);
  const pageSize = 6;
  const paginatedAvailable = availableTables.slice((page - 1) * pageSize, page * pageSize);

  // Reset to first page when availableTables list shrinks (avoid empty page)
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(availableTables.length / pageSize));
    if (page > maxPage) setPage(maxPage);
  }, [availableTables.length, page]);

  return (
    <>
      <div style={{ marginBottom: '32px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <DatabaseOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
            <h3 style={{
              fontWeight: '600',
              fontSize: '18px',
              color: '#262626',
              margin: '0'
            }}>
              Select Data Source
            </h3>
          </div>
          {/* Search Input */}
          <div style={{ width: '300px' }}>
            <Input
              placeholder="Search data sources..."
              prefix={<SearchOutlined style={{ color: '#8c8c8c' }} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{
                borderRadius: '6px',
                fontSize: '14px'
              }}
              size="medium"
            />
          </div>
        </div>
        <p style={{
          fontSize: '14px',
          color: '#595959',
          margin: '0 0 24px 0'
        }}>
          Choose one Model for your report
        </p>

        {/* Loading Spinner */}
        {loading && !hasGlobalLoadedOnce && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            textAlign: 'center'
          }}>
            <Spin size="large" />
            <p style={{
              fontSize: '16px',
              color: '#8c8c8c',
              margin: '16px 0 0 0',
              fontWeight: '500'
            }}>
              Loading data sources...
            </p>
          </div>
        )}

        {/* No search results */}
        {searchText.trim() && allTables.length === 0 && !loading && hasGlobalLoadedOnce && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            textAlign: 'center'
          }}>
            <SearchOutlined style={{
              fontSize: '48px',
              color: '#d9d9d9',
              marginBottom: '16px'
            }} />
            <h4 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#262626',
              margin: '0 0 8px 0'
            }}>
              No data sources found
            </h4>
            <p style={{
              fontSize: '14px',
              color: '#8c8c8c',
              margin: '0 0 16px 0',
              maxWidth: '300px',
              lineHeight: '1.5'
            }}>
              No data sources match your search for "{searchText}"
            </p>
            <Button
              type="text"
              onClick={() => setSearchText('')}
              style={{ color: '#1890ff' }}
            >
              Clear search
            </Button>
          </div>
        )}

        {/* Content when not loading */}
        {(!loading || hasGlobalLoadedOnce) && !(searchText.trim() && allTables.length === 0) && (
          <>
            {/* No models available state */}
            {allTables.length === 0 && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '80px 20px',
                textAlign: 'center'
              }}>
                <DatabaseOutlined style={{
                  fontSize: '64px',
                  color: '#d9d9d9',
                  marginBottom: '24px'
                }} />
                <h4 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#262626',
                  margin: '0 0 8px 0'
                }}>
                  No models available
                </h4>
                <p style={{
                  fontSize: '14px',
                  color: '#8c8c8c',
                  margin: '0',
                  maxWidth: '300px',
                  lineHeight: '1.5'
                }}>
                  No data models were found. Please contact your administrator to set up data sources.
                </p>
              </div>
            )}

            {/* Models available */}
            {allTables.length > 0 && (
              <>
                {/* Selected Tables Section */}
                {selectedTableObjects.length > 0 && (
                  <>
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#262626',
                      margin: '0 0 16px 0'
                    }}>
                      Selected Model
                    </h4>
                    <div style={{ marginBottom: '32px' }}>
                      {selectedTableObjects.map(table => (
                        <TableCard
                          key={table.id}
                          {...table}
                          isSelected={true}
                          onSelect={() => { }}
                          onRemove={() => onRemoveTable && onRemoveTable(table.id)}
                        />
                      ))}
                    </div>
                  </>
                )}

                {/* Available Tables Section */}
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#262626',
                  margin: '0 0 16px 0'
                }}>
                  Available Models
                </h4>
                <Row gutter={[16, 16]} style={{ display: 'flex', flexWrap: 'wrap' }}>
                  {paginatedAvailable.map(table => (
                    <Col
                      key={table.id}
                      xs={24}
                      sm={24}
                      lg={12}
                      style={{
                        display: 'flex',
                        width: '100%'
                      }}
                    >
                      <TableCard
                        {...table}
                        isSelected={false}
                        onSelect={() => onSelectTable && onSelectTable(table.id)}
                        onRemove={() => { }}
                      />
                    </Col>
                  ))}
                </Row>
                {availableTables.length > pageSize && (
                  <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                    <Pagination current={page} pageSize={pageSize} total={availableTables.length} onChange={(p) => setPage(p)} size="small" />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

// --- STEP 2: Fields ---
const FieldBadge = ({ name, type, onClose }) => (
  <span className="field-badge">
    <span className="field-name">{name}</span>
    <span className={`field-type ${type.toLowerCase()}`}>{type}</span>
    <CloseOutlined className="close-icon" onClick={onClose} />
  </span>
);

const AvailableFieldCard = ({ fieldName, tableName, description, fieldType = 'dimension', onClick, isSelected = false }) => (
  <div
    className={`available-field-card ${isSelected ? 'selected-field' : ''}`}
    onClick={isSelected ? undefined : onClick}
    style={{
      opacity: isSelected ? 0.5 : 1,
      cursor: isSelected ? 'not-allowed' : 'pointer',
      filter: isSelected ? 'blur(0.1px)' : 'none',
      pointerEvents: isSelected ? 'none' : 'auto'
    }}
  >
    <div className="field-name-row">
      <span className={`field-dot ${fieldType}`} />
      <span className="field-name">{fieldName}</span>
    </div>
    <span className="field-table-name">{tableName}</span>
    <p className="field-description">{description}</p>
    {isSelected && (
      <div style={{
        position: 'absolute',
        top: '8px',
        right: '8px',
        backgroundColor: '#52c41a',
        color: 'white',
        borderRadius: '50%',
        width: '20px',
        height: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        fontWeight: 'bold'
      }}>
        ✓
      </div>
    )}
  </div>
);

const Step2Content = ({
  selectedFields,
  onRemoveField,
  onAddField,
  selectedTables = [],
  fieldsToDisplay,
  onFieldsToDisplayChange,
  setAggregrationType,
  aggregrationType,
  isDetail,
  setIsDetail,
  is_collapsed,
  setIs_collapsed,
  sortBy,
  setSortBy
}) => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [dimensionFields, setDimensionFields] = useState([]);
  const [measureFields, setMeasureFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dimensionPage, setDimensionPage] = useState(1);
  const [measurePage, setMeasurePage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [fields, setFields] = useState([]);

  const pageSize = 6;

  const SORTABLE_TYPES = [
  "string",
  "integer",
  "decimal",
  "currency",
  "date",
  "datetime",
  "percentage",
  "lookup",
  "formula"
];





  console.log('fields to display in step2', fieldsToDisplay);
  const selectedObjectName = selectedTables.length > 0 ? selectedTables[0] : null;

  useEffect(() => {
    if (selectedObjectName) {
      fetchFields(selectedObjectName);
    }
  }, [selectedObjectName]);

    const fetchFields = async (objectName) => {
    try {
      const apiService = new ApiService(
        `${BASE_URL}/mt_fields/object/${objectName}`,
        { 'Content-Type': 'application/json' },
        'GET',
      );

      const response = await apiService.makeCall();
      const filteredFields = response.filter(field => field.name !== 'recordCount');
      setFields(filteredFields);
    } catch (error) {
      message.error("Failed to fetch fields.");
    }
  };


  const sortableFields = fields.filter(f =>
  SORTABLE_TYPES.includes(f.type?.toLowerCase())
);

  // Fetch fields when selected object changes
  useEffect(() => {
    if (selectedObjectName) {
      fetchFieldsGlobally(selectedObjectName, () => { }, setDimensionFields, setMeasureFields, setLoading);
    }
  }, [selectedObjectName]);

  // Reset pagination when fields change (when switching objects)
  useEffect(() => {
    setDimensionPage(1);
    setMeasurePage(1);
  }, [selectedObjectName]);

  // Reset pagination when filter changes
  useEffect(() => {
    setDimensionPage(1);
    setMeasurePage(1);
  }, [selectedFilter]);

  // Reset pagination when search changes
  useEffect(() => {
    setDimensionPage(1);
    setMeasurePage(1);
  }, [searchText]);

  // Filter fields based on search text
  const filterFieldsBySearch = (fields) => {
    if (!searchText.trim()) return fields;

    const searchLower = searchText.toLowerCase().trim();
    return fields.filter(field =>
      (field.name || field.fieldName)?.toLowerCase().includes(searchLower) ||
      (field.label || field.description)?.toLowerCase().includes(searchLower)
    );
  };

  // Auto-populate fieldsToDisplay with selectedFields (dimensions and measures)
  useEffect(() => {
    if (selectedFields && selectedFields.length > 0 && onFieldsToDisplayChange) {
      const fieldNames = selectedFields.map(field => field.name.split('.').pop()); // Remove table prefix

      // Only update if the current fieldsToDisplay doesn't already contain all selected fields
      if (!fieldsToDisplay || !fieldNames.every(field => fieldsToDisplay.includes(field))) {
        // Merge existing fieldsToDisplay with new selectedFields, avoiding duplicates
        const existingAdditionalFields = fieldsToDisplay ? fieldsToDisplay.filter(field => !fieldNames.includes(field)) : [];
        const newFieldsToDisplay = [...fieldNames, ...existingAdditionalFields];
        onFieldsToDisplayChange(newFieldsToDisplay);
      }
    }
  }, [selectedFields, onFieldsToDisplayChange, fieldsToDisplay]);

  // Create options for DynamicPicklistComponent with disabled state for required fields
  const getSelectOptions = () => {
    const allFields = [...dimensionFields, ...measureFields];
    const selectedFieldNames = selectedFields.map(field => field.name.split('.').pop());

    return allFields.map(field => ({
      ...field,
      disabled: selectedFieldNames.includes(field.name) // Disable selected dimensions/measures
    }));
  };

  // Transform API fields to match the expected format for rendering
  const transformedDimensionFields = filterFieldsBySearch(dimensionFields).map(field => ({
    fieldName: field.name || field.fieldName,
    tableName: selectedObjectName || 'Object',
    description: field.label || field.description || 'Database field',
    fieldType: 'dimension'
  }));

  const transformedMeasureFields = filterFieldsBySearch(measureFields).map(field => ({
    fieldName: field.name || field.fieldName,
    tableName: selectedObjectName || 'Object',
    description: field.label || field.description || 'Database field',
    fieldType: 'measure'
  }));

  const renderFieldCards = (fields, currentPage, setCurrentPage) => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedFields = fields.slice(startIndex, endIndex);

    return paginatedFields?.map(field => {
      const isSelected = selectedFields?.some(f => f.name === `${selectedObjectName}.${field?.fieldName}`);
      return (
        <Col key={field?.fieldName} xs={24} sm={12} lg={8}>
          <AvailableFieldCard
            {...field}
            onClick={() => onAddField(field)}
            isSelected={isSelected}
          />
        </Col>
      );
    });
  };

  // Show loading state
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        textAlign: 'center'
      }}>
        <Spin size="large" />
        <p style={{
          fontSize: '16px',
          color: '#8c8c8c',
          margin: '16px 0 0 0',
          fontWeight: '500'
        }}>
          Loading fields...
        </p>
      </div>
    );
  }

  // Show message if no object is selected
  if (!selectedObjectName) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 20px',
        textAlign: 'center'
      }}>
        <DatabaseOutlined style={{
          fontSize: '64px',
          color: '#d9d9d9',
          marginBottom: '24px'
        }} />
        <h4 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#262626',
          margin: '0 0 8px 0'
        }}>
          No model selected
        </h4>
        <p style={{
          fontSize: '14px',
          color: '#8c8c8c',
          margin: '0',
          maxWidth: '300px',
          lineHeight: '1.5'
        }}>
          Please go back to Step 1 and select a data model to see available fields.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Fields to Display Section */}
      <div style={{ marginTop: '32px', marginBottom: '32px' }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#262626',
          marginBottom: '8px'
        }}>
          Fields to Display
        </h3>
        <p style={{
          fontSize: '14px',
          color: '#595959',
          margin: '0 0 16px 0',
          lineHeight: '1.4'
        }}>
          Select which fields should be displayed in your report output
        </p>
        <DynamicPicklistComponent
          onChange={onFieldsToDisplayChange}
          isSelect={true}
          selectedFields={fieldsToDisplay}
          selectOptions={getSelectOptions()}
        />
      </div>
      <h3 style={{
        fontSize: '18px',
        fontWeight: '600',
        color: '#262626',
        marginBottom: '16px'
      }}>
        Selected Fields ({selectedFields?.length})
      </h3>
      <div className="selected-fields-container">
        {selectedFields?.map((field, index) => (
          <FieldBadge
            key={field?.name}
            name={field?.name}
            type={field?.type}
            onClose={() => onRemoveField(field?.name)}
          />
        ))}
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '32px',
        marginBottom: '16px'
      }}>
        <h4 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#262626',
          marginBottom: '16px'
        }}>
          Available Fields
        </h4>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          {/* Search Input */}
          <div style={{ width: '250px', marginTop: '16px' }}>
            <Input
              placeholder="Search fields..."
              prefix={<SearchOutlined style={{ color: '#8c8c8c' }} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{
                borderRadius: '6px',
                fontSize: '14px'
              }}
              size="medium"
            />
          </div>
          <div className="field-type-selector">
            <Button
              type={selectedFilter === 'all' ? 'primary' : 'default'}
              onClick={() => setSelectedFilter('all')}
            >
              All
            </Button>
            <Button
              type={selectedFilter === 'dimensions' ? 'primary' : 'default'}
              onClick={() => setSelectedFilter('dimensions')}
            >
              Dimensions
            </Button>
            <Button
              type={selectedFilter === 'measures' ? 'primary' : 'default'}
              onClick={() => setSelectedFilter('measures')}
            >
              Measures
            </Button>
          </div>
        </div>
      </div>

      {(selectedFilter === 'all' || selectedFilter === 'dimensions') && (
        <>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '24px',
            marginBottom: '8px'
          }}>
            <span style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#1890ff'
            }} />
            <h4 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#262626',
              margin: '0'
            }}>
              Dimensions ({transformedDimensionFields.length})
            </h4>
          </div>
          <p style={{
            fontSize: '14px',
            color: '#595959',
            margin: '0 0 16px 0',
            lineHeight: '1.4'
          }}>
            Categorical data used to group, filter, and organize your report
          </p>
          <Row gutter={[16, 16]}>
            {renderFieldCards(transformedDimensionFields, dimensionPage, setDimensionPage)}
          </Row>
          {transformedDimensionFields.length === 0 && searchText.trim() && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 20px',
              textAlign: 'center',
              backgroundColor: '#fafafa',
              borderRadius: '6px',
              margin: '16px 0'
            }}>
              <SearchOutlined style={{
                fontSize: '32px',
                color: '#d9d9d9',
                marginBottom: '12px'
              }} />
              <p style={{
                fontSize: '14px',
                color: '#8c8c8c',
                margin: '0',
                lineHeight: '1.4'
              }}>
                No dimension fields match "{searchText}"
              </p>
            </div>
          )}
          {transformedDimensionFields.length > pageSize && (
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <Pagination
                current={dimensionPage}
                pageSize={pageSize}
                total={transformedDimensionFields.length}
                onChange={(page) => setDimensionPage(page)}
                size="small"
                showSizeChanger={false}
                showQuickJumper={false}
                showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} dimensions`}
              />
            </div>
          )}
        </>
      )}

      {(selectedFilter === 'all' || selectedFilter === 'measures') && (
        <>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '32px',
            marginBottom: '8px'
          }}>
            <span style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#52c41a'
            }} />
            <h4 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#262626',
              margin: '0'
            }}>
              Measures ({transformedMeasureFields.length})
            </h4>
          </div>
          <p style={{
            fontSize: '14px',
            color: '#595959',
            margin: '0 0 16px 0',
            lineHeight: '1.4'
          }}>
            Numeric data that can be measured, aggregated, and calculated
          </p>
          <Row gutter={[16, 16]}>
            {renderFieldCards(transformedMeasureFields, measurePage, setMeasurePage)}
          </Row>
          {transformedMeasureFields.length === 0 && searchText.trim() && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 20px',
              textAlign: 'center',
              backgroundColor: '#fafafa',
              borderRadius: '6px',
              margin: '16px 0'
            }}>
              <SearchOutlined style={{
                fontSize: '32px',
                color: '#d9d9d9',
                marginBottom: '12px'
              }} />
              <p style={{
                fontSize: '14px',
                color: '#8c8c8c',
                margin: '0',
                lineHeight: '1.4'
              }}>
                No measure fields match "{searchText}"
              </p>
            </div>
          )}
          {transformedMeasureFields.length > pageSize && (
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <Pagination
                current={measurePage}
                pageSize={pageSize}
                total={transformedMeasureFields.length}
                onChange={(page) => setMeasurePage(page)}
                size="small"
                showSizeChanger={false}
                showQuickJumper={false}
                showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} measures`}
              />
            </div>
          )}
        </>
      )}

      {!isDetail &&
        measureFields?.length === 0 && (
          <p style={{ color: "red", marginTop: "4px" }}>
            Please select at least one measure field.
          </p>
        )
      }

      {/* Aggregation Type + Is Detail */}
      <div
        style={{
          marginTop: "5px",
          padding: "10px",
          border: "1px solid #f0f0f0",
          borderRadius: "8px",
          background: "#fafafa"
        }}
      >
        <Title level={4} style={{ marginTop: 0, marginBottom: "5px" }}>
          Report Settings
        </Title>

        {/* Two-Column Layout */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "24px",
            alignItems: "center"
          }}
        >
          {/* Column 1 – Aggregation Type */}
          <div>
            <label style={{ fontSize: "14px", fontWeight: 500 }}>Aggregation Type</label>
            <Select
              allowClear
              placeholder="Select aggregation type"
              style={{ width: "100%", marginTop: "8px" }}
              value={aggregrationType}
              onChange={setAggregrationType}
              status={!isDetail && !aggregrationType ? "error" : ""}  // 🔥 Only show error, no disabling
              options={[
                { label: "SUM", value: "sum" },
                { label: "AVG", value: "avg" },
                { label: "COUNT", value: "count" },
                { label: "MIN", value: "min" },
                { label: "MAX", value: "max" }
              ]}
            />
            {!isDetail && !aggregrationType && (
              <p style={{ color: "red", marginTop: "4px" }}>Aggregation type is required.</p>
            )}
          </div>

          {/* Column 2 – Show Detail */}
          <div>
            <label style={{ fontSize: "14px", fontWeight: 500 }}>Show Detail</label>
            <div style={{ marginTop: "8px" }}>
              <Switch checked={isDetail} onChange={setIsDetail} />
            </div>
          </div>
        </div>
      </div>

      {/* Collapsed + Sort By Section */}
      <div
        style={{
          marginTop: "20px",
          padding: "10px",
          border: "1px solid #f0f0f0",
          borderRadius: "8px",
          background: "#fafafa"
        }}
      >
        <Title level={4} style={{ marginTop: 0, marginBottom: "5px" }}>
          Display Options
        </Title>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "24px",
            alignItems: "center"
          }}
        >
          {/* Column 1 – Collapsed Boolean */}
          <div>
            <label style={{ fontSize: "14px", fontWeight: 500 }}>Collapsed</label>
            <div style={{ marginTop: "8px" }}>
              <Switch checked={is_collapsed} onChange={setIs_collapsed} />
            </div>
          </div>

          {/* Column 2 – Sort By Select */}
          <div>
            <label style={{ fontSize: "14px", fontWeight: 500 }}>Sort By</label>
            <Select
              allowClear
              placeholder="Select field to sort"
              style={{ width: "100%", marginTop: "8px" }}
              value={sortBy}
              onChange={setSortBy}
              options={
              sortableFields.map(f => ({
                label: f.name.split(".").pop(),
                value: f.name
              }))
            }

            />
          </div>
        </div>
      </div>

    </>


  );
};

// --- STEP 3: Filters ---
const Step3Content = ({ filters, selectedTables = [], setFilters, logic, setLogic,selectedFilters }) => {
  const [lookupOptions, setLookupOptions] = useState([]);
  const [valueOperator, setValueOperator] = useState('');
  const [fields, setFields] = useState([]);
  const [showFiltersComponent, setShowFiltersComponent] = useState(false);
  const [hasInitializedFilters, setHasInitializedFilters] = useState(false);


  // Get the selected object name to fetch fields
  const selectedObjectName = selectedTables.length > 0 ? selectedTables[0] : null;

  // Fetch fields when selected object changes
  useEffect(() => {
    if (selectedObjectName) {
      fetchFields(selectedObjectName);
    }
  }, [selectedObjectName]);

  // Show filters component if there are existing filters
  // Run this AFTER fields are loaded
 

  useEffect(() => {
    const hasValidFilters = Object.values(selectedFilters || {}).some(
      f => f && Object.entries(f).length > 0
    );
    if (!hasInitializedFilters && fields.length > 0 && hasValidFilters) {
      setShowFiltersComponent(true);

      // console.log("Selected Filters in Step3Content:", selectedFilters);

      const existingFilters = filters
        ? Object.entries(selectedFilters).map(([key, { field, value }]) => {
          let operator = '';
          let actualValue = '';
          if (typeof value === 'object' && value !== null) {
            operator = Object.keys(value)[0]; // Extract operator ("$lt")
            actualValue = value[operator]; // Extract the actual value (20)
          } else {
            actualValue = value;
          }
          let op = '';
          if (operator !== '') {
            try {
              setValueOperator(operator);
              op = operator;
            } catch (error) {
              // Handle the error gracefully
              setValueOperator(''); // or any default value
            }
          } else {
            op = '';
          }
          return {
            field,
            value: value,
            displayValue: actualValue,
            operator: op
          };
        })
        : [{ field: '', value: '', operator: '' }];
      setFilters(existingFilters); // Set filters state here
      fetchLookupRecordsForExistingFilters(existingFilters); 

      // Prevent running again
      setHasInitializedFilters(true);
    }
  }, [fields]);

  const fetchFields = async (objectName) => {
    try {
      const apiService = new ApiService(
        `${BASE_URL}/mt_fields/object/${objectName}`,
        { 'Content-Type': 'application/json' },
        'GET',
      );

      const response = await apiService.makeCall();
      const filteredFields = response.filter(field => field.name !== 'recordCount');
      setFields(filteredFields);
    } catch (error) {
      message.error("Failed to fetch fields.");
    }
  };

  const fetchLookupRecordsForExistingFilters = (existingFilters) => {
    console.log("Existing Filters:", existingFilters);
    console.log("Fields:", fields);
    existingFilters.forEach(filter => {
      const selectedField = fields.find(field => field.name === filter?.field?.replace('_id', ''));
      if (selectedField && selectedField.type === 'lookup') {
        fetchLookupRecords(selectedField.parent_object_name);
      }
    });
  };

  const fetchLookupRecords = async (objectName) => {
    try {
      const apiServiceForRecords = new ApiService(
        `${BASE_URL}/fetch_records/${objectName}`,
        { 'Content-Type': 'application/json' },
        'GET'
      );
      const response = await apiServiceForRecords.makeCall();

      const records = response?.records?.map(record => ({
        value: record._id,
        label: record.Name,
      }));
      setLookupOptions(records);
    } catch (error) {
      const errorMessage = error && typeof error === 'object'
        ? Object.entries(error).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`).join(' | ')
        : 'Failed to fetch lookup records due to an unknown error';
      message.error(errorMessage);
    }
  };

  // const setFilters = (newFilters) => {
  //   // This function should update filters in the parent component
  //   // For now, we'll use the existing onFilterChange if available
  //   if (onFilterChange) {
  //     // Convert the filters format if needed
  //     console.log('New filters:', newFilters);
  //   }
  // };

  const setValueOperatorLocal = (operator) => {
    setValueOperator(operator);
  };

  return (
    <>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px'
      }}>
        <FilterOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
        <h3 style={{
          fontWeight: '600',
          fontSize: '18px',
          color: '#262626',
          margin: '0'
        }}>
          Filters
        </h3>
      </div>
      <p style={{
        fontSize: '14px',
        color: '#595959',
        margin: '0 0 32px 0'
      }}>
        Add filters to customize what data appears in your report
      </p>

      {!selectedObjectName ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 20px',
          textAlign: 'center'
        }}>
          <FilterOutlined style={{
            fontSize: '48px',
            color: '#d9d9d9',
            marginBottom: '16px'
          }} />
          <h4 style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#262626',
            margin: '0 0 8px 0'
          }}>
            No model selected
          </h4>
          <p style={{
            fontSize: '14px',
            color: '#8c8c8c',
            margin: '0',
            maxWidth: '280px'
          }}>
            Please select a data model in Step 1 to add filters
          </p>
        </div>
      ) : !showFiltersComponent && (!filters || filters.length === 0) ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 20px',
          textAlign: 'center'
        }}>
          <FilterOutlined style={{
            fontSize: '48px',
            color: '#d9d9d9',
            marginBottom: '16px'
          }} />
          <h4 style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#262626',
            margin: '0 0 8px 0'
          }}>
            No filters applied
          </h4>
          <p style={{
            fontSize: '14px',
            color: '#8c8c8c',
            margin: '0 0 24px 0',
            maxWidth: '280px'
          }}>
            Add filters to show only the data that matters to you
          </p>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => setShowFiltersComponent(true)}
          >
            Add Filter
          </Button>
        </div>
      ) : (
        <>
          <FiltersDynamic
            heading={" "}
            fields={fields}
            logic={logic}
            setLogic={setLogic}
            filters={filters}
            setFilters={setFilters}
            setValueOperator={setValueOperatorLocal}
            valueOperator={valueOperator}
            fetchLookupRecordsForExistingFilters={fetchLookupRecordsForExistingFilters}
            fetchLookupRecords={fetchLookupRecords}
            lookupOptions={lookupOptions}
          />
        </>
      )}
    </>
  );
};

// --- STEP 4: Chart ---
const ChartTypeCard = ({ icon, title, description, recommended, isSelected, onClick, chartType }) => {
  const [isHovered, setIsHovered] = useState(false);

  const getMiniChart = () => {
    switch (chartType) {
      case 'table':
        return <MiniTableIcon />;
      case 'column':
        return <MiniBarChart />;
      case 'line':
        return <MiniLineChart />;
      case 'pie':
        return <MiniPieChart />;
      default:
        return icon;
    }
  };

  return (
    <div
      className={`chart-type-card ${recommended ? 'recommended' : ''} ${isSelected ? 'selected' : ''}`}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        border: isSelected ? '2px solid #1890ff' : isHovered ? '1px solid #1890ff' : '1px solid #e8e8e8',
        borderRadius: '8px',
        padding: '12px',
        cursor: 'pointer',
        backgroundColor: isSelected ? '#f0f9ff' : isHovered ? '#fafbff' : '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        transition: 'all 0.3s ease',
        position: 'relative',
        height: '140px',
        boxShadow: isHovered && !isSelected ? '0 2px 8px rgba(0, 0, 0, 0.1)' : isSelected ? '0 2px 8px rgba(24, 144, 255, 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.05)',
        width: '100%',
        overflow: 'hidden'
      }}
    >
      {recommended && (
        <span style={{
          position: 'absolute',
          top: '6px',
          right: '6px',
          backgroundColor: '#52c41a',
          color: 'white',
          fontSize: '10px',
          fontWeight: '500',
          padding: '2px 4px',
          borderRadius: '3px'
        }}>
          high
        </span>
      )}
      <div style={{
        height: '50px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '12px',
        transition: 'all 0.3s ease',
        flexShrink: 0
      }}>
        {isHovered || isSelected ? getMiniChart() : (
          <div style={{
            fontSize: '28px',
            color: isSelected ? '#1890ff' : '#8c8c8c',
            transition: 'color 0.3s ease'
          }}>
            {icon}
          </div>
        )}
      </div>
      <div style={{
        fontSize: '13px',
        fontWeight: '600',
        color: isSelected ? '#1890ff' : '#262626',
        marginBottom: '4px',
        transition: 'color 0.3s ease',
        lineHeight: '1.2',
        textAlign: 'center'
      }}>
        {title}
      </div>
      <p style={{
        fontSize: '11px',
        color: '#8c8c8c',
        margin: '0',
        lineHeight: '1.3',
        textAlign: 'center',
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        flex: 1
      }}>
        {description}
      </p>
    </div>
  );
};

const RecommendedChartCard = ({ icon, title, description, isSelected, onClick, chartType }) => {
  const [isHovered, setIsHovered] = useState(false);

  const getLargeChart = () => {
    switch (chartType) {
      case 'table':
        return (
          <div style={{
            height: '60px',
            width: '80px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            justifyContent: 'center'
          }}>
            {[1, 2, 3, 4].map(i => (
              <div key={i} style={{
                height: '10px',
                backgroundColor: isSelected ? '#1890ff' : '#8c8c8c',
                borderRadius: '2px',
                opacity: i === 1 ? 1 : 0.7,
                transition: 'all 0.3s ease'
              }} />
            ))}
          </div>
        );
      default:
        return (
          <div style={{
            fontSize: '48px',
            color: isSelected ? '#1890ff' : '#8c8c8c',
            transition: 'color 0.3s ease'
          }}>
            {icon}
          </div>
        );
    }
  };

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        border: isSelected ? '2px solid #1890ff' : isHovered ? '1px solid #1890ff' : '1px solid #e8e8e8',
        borderRadius: '8px',
        padding: '24px',
        cursor: 'pointer',
        backgroundColor: isSelected ? '#f0f9ff' : isHovered ? '#fafbff' : '#ffffff',
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        transition: 'all 0.3s ease',
        position: 'relative',
        boxShadow: isHovered && !isSelected ? '0 2px 8px rgba(0, 0, 0, 0.1)' : isSelected ? '0 2px 8px rgba(24, 144, 255, 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.05)',
        width: '100%',
        maxWidth: '100%'
      }}
    >
      <span style={{
        position: 'absolute',
        top: '12px',
        right: '12px',
        backgroundColor: '#52c41a',
        color: 'white',
        fontSize: '11px',
        fontWeight: '500',
        padding: '3px 8px',
        borderRadius: '4px'
      }}>
        high
      </span>
      <div style={{ flexShrink: 0 }}>
        {getLargeChart()}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{
          fontSize: '18px',
          fontWeight: '600',
          color: isSelected ? '#1890ff' : '#262626',
          marginBottom: '8px',
          transition: 'color 0.3s ease'
        }}>
          {title}
        </div>
        <p style={{
          fontSize: '14px',
          color: '#8c8c8c',
          margin: '0',
          lineHeight: '1.4'
        }}>
          {description}
        </p>
      </div>
    </div>
  );
};

const Step4Content = ({ selectedChartType = 'table', onSelectChartType }) => (
  <>
    <h3 style={{
      fontSize: '20px',
      fontWeight: '600',
      color: '#262626',
      margin: '0 0 24px 0'
    }}>
      Choose Chart Type
    </h3>

    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '16px'
    }}>
      <span style={{ fontSize: '16px', color: '#fa8c16' }}>✨</span>
      <h4 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#262626',
        margin: '0'
      }}>
        Recommended for your data
      </h4>
    </div>

    <div style={{ marginBottom: '32px' }}>
      <RecommendedChartCard
        icon={<TableOutlined />}
        title="Table"
        description="Tables work with any field combination and provide detailed data views"
        isSelected={selectedChartType === 'table'}
        onClick={() => onSelectChartType && onSelectChartType('table')}
        chartType="table"
      />
    </div>

    <h4 style={{
      fontSize: '16px',
      fontWeight: '600',
      color: '#262626',
      margin: '0 0 16px 0'
    }}>
      All chart types
    </h4>

    <Row gutter={[16, 16]}>
      <Col xs={12} sm={6} md={6} lg={6}>
        <ChartTypeCard
          icon={<TableOutlined />}
          title="Table"
          description="Rows and columns of data"
          isSelected={selectedChartType === 'table'}
          onClick={() => onSelectChartType && onSelectChartType('table')}
          chartType="table"
        />
      </Col>
      <Col xs={12} sm={6} md={6} lg={6}>
        <ChartTypeCard
          icon={<BarChartOutlined />}
          title="Column Chart"
          description="Compare values across categories"
          isSelected={selectedChartType === 'column'}
          onClick={() => onSelectChartType && onSelectChartType('column')}
          chartType="column"
        />
      </Col>
      <Col xs={12} sm={6} md={6} lg={6}>
        <ChartTypeCard
          icon={<LineChartOutlined />}
          title="Line Chart"
          description="Show trends over time"
          isSelected={selectedChartType === 'line'}
          onClick={() => onSelectChartType && onSelectChartType('line')}
          chartType="line"
        />
      </Col>
      <Col xs={12} sm={6} md={6} lg={6}>
        <ChartTypeCard
          icon={<PieChartOutlined />}
          title="Pie Chart"
          description="Parts of a whole"
          isSelected={selectedChartType === 'pie'}
          onClick={() => onSelectChartType && onSelectChartType('pie')}
          chartType="pie"
        />
      </Col>
    </Row>
  </>
);

// --- STEP 5: Preview ---
const Step5Content = ({ hasRunReport, reportData, loading, fieldsToDisplay, selectedTables, fields, selectedFields, reportGroupFields, reportAggregationFields, selectedChartType = 'table' }) => {
  const [expandedGroupIds, setExpandedGroupIds] = useState(new Set());

  // Debug logging
  console.log("Step5Content props:", {
    hasRunReport,
    reportDataLength: reportData?.length,
    loading,
    fieldsToDisplay,
    selectedTables,
    selectedChartType,
    reportGroupFields,
    reportAggregationFields
  });

  // Get grouping fields from report configuration
  const getGroupingFields = () => {
    // If we have reportGroupFields from the parent component, use those
    if (reportGroupFields && Array.isArray(reportGroupFields) && reportGroupFields.length > 0) {
      return reportGroupFields;
    }

    // Fallback: Get dimension fields from global cache for grouping
    const selectedObjectName = selectedTables.length > 0 ? selectedTables[0] : null;

    if (!selectedObjectName || !globalFieldsCache[selectedObjectName]) {
      return [];
    }

    // Get dimension fields from global cache
    const dimensionFields = globalFieldsCache[selectedObjectName].dimensionFields || [];

    // Filter to only include fields that are in fieldsToDisplay and were selected
    return dimensionFields
      .map(field => field.name)
      .filter(fieldName => fieldsToDisplay?.includes(fieldName))
      .filter(fieldName => selectedFields?.some(sf => sf.name.split('.').pop() === fieldName));
  };

  // Get aggregation fields from report configuration
  const getAggregationFields = () => {
    // If we have reportAggregationFields from the parent component, use those
    if (reportAggregationFields && Array.isArray(reportAggregationFields) && reportAggregationFields.length > 0) {
      return reportAggregationFields;
    }

    // Fallback: Get measure fields from global cache
    const selectedObjectName = selectedTables.length > 0 ? selectedTables[0] : null;

    if (!selectedObjectName || !globalFieldsCache[selectedObjectName]) {
      return [];
    }

    // Get measure fields from global cache
    const measureFields = globalFieldsCache[selectedObjectName].measureFields || [];

    // Filter to only include fields that are in fieldsToDisplay and were selected
    return measureFields
      .map(field => field.name)
      .filter(fieldName => fieldsToDisplay?.includes(fieldName))
      .filter(fieldName => selectedFields?.some(sf => sf.name.split('.').pop() === fieldName));
  };

  const selectedGroupFields = getGroupingFields();
  const selectedAggregationFields = getAggregationFields();
  console.log("Selected group fields:", selectedGroupFields);
  console.log("Selected aggregation fields:", selectedAggregationFields);


  // Generate columns with sophisticated rendering like ReportDisplay.js
  const generateColumns = (data) => {
    if (!data || data.length === 0) return [];

    const firstRow = data[0];
    const fieldNames = fieldsToDisplay && fieldsToDisplay.length > 0
      ? fieldsToDisplay.filter(field => firstRow.hasOwnProperty(field))
      : Object.keys(firstRow);

    return fieldNames.map((fieldName, index) => {
      // Find field metadata for this field
      const fieldMeta = fields?.find(f => f.name === fieldName);

      return {
        key: fieldName,
        name: fieldName,
        field: fieldName,
        headerName: fieldName,
        width: 150,
        // resizable: true,
        sortable: true,

        // Currency field rendering
        ...(fieldMeta?.type === "currency" && {
          renderCell: ({ row }) => {
            const value = row[fieldName];
            const formattedValue = value % 1 !== 0
              ? Number(value).toFixed(2)
              : `${value}.00`;
            return (
              <span>
                ${formattedValue}
              </span>
            );
          },
        }),

        // Decimal field rendering
        ...(fieldMeta?.type === "decimal" && {
          renderCell: ({ row }) => {
            const value = row[fieldName];
            const formattedValue = value % 1 !== 0
              ? Number(value).toFixed(2)
              : `${value}.00`;
            return (
              <span>
                {formattedValue}
              </span>
            );
          },
        }),

        // Integer field rendering
        ...(fieldMeta?.type === "integer" && {
          renderCell: ({ row }) => {
            return <span>{row[fieldName]}</span>;
          },
        }),

        // Name field with navigation
        ...(fieldName === "Name" && {
          renderCell: ({ row }) => {
            const selectedObjectName = selectedTables.length > 0 ? selectedTables[0] : null;
            const redirectUrl = selectedObjectName ? `/record/${selectedObjectName}/${row['_id']}` : null;
            const value = row[fieldName];
            return (
              <span
                style={{ color: '#00A1E0', textDecoration: 'underline', cursor: 'pointer' }}
                onClick={() => redirectUrl && (window.location.href = redirectUrl)}
              >
                {value}
              </span>
            );
          },
        }),

        // Date field rendering
        ...(fieldMeta?.type === "date" && {
          renderCell: ({ row }) => {
            const date = row[fieldName];
            return <span>{date ? dayjs(date).format('DD/MM/YYYY') : ''}</span>;
          },
        }),

        // DateTime field rendering
        ...(fieldMeta?.type === "datetime" && {
          renderCell: ({ row }) => {
            const date = row[fieldName];
            return <span>{date ? dayjs(date).format('DD/MM/YYYY hh:mm A') : ''}</span>;
          },
        }),

        // Lookup field rendering
        ...(fieldMeta?.type === "lookup" && {
          renderCell: ({ row }) => {
            const parentObjectName = fieldMeta?.parent_object_name;
            console.log("Rendering lookup field:", fieldName, "with parent object:", parentObjectName);


            // Support multiple structures: object with _id, object in *_object, or string
            const rawValue = row[fieldName];
            const objectValue = typeof rawValue === "object" && rawValue !== null
              ? rawValue
              : row[`${fieldName}_object`] ?? null;

            const parentRecordId = objectValue?._id;
            const name = typeof rawValue === "string"
              ? rawValue
              : objectValue?.Name || rawValue?.Name || "N/A";

            const redirectUrl = parentRecordId && parentObjectName
              ? `/record/${parentObjectName}/${parentRecordId}`
              : null;

            return (
              <span
                style={{
                  color: redirectUrl ? "#00A1E0" : undefined,
                  textDecoration: redirectUrl ? "underline" : undefined,
                  cursor: redirectUrl ? "pointer" : undefined,
                }}
                onClick={() => redirectUrl && window.open(redirectUrl, "_blank")}
              >
                {name}
              </span>
            );
          }
        }),

        // Address field rendering
        ...(fieldMeta?.type === "address" && {
          renderCell: ({ row }) => {
            const address = row[fieldName];
            if (address) {
              const addressParts = [
                address.street,
                address.city,
                address.state,
                address.postalCode,
                address.country
              ].filter(Boolean);
              return <span>{addressParts.join(', ')}</span>;
            }
            return <span></span>;
          },
        }),

        // Default rendering for other field types
        ...((!fieldMeta?.type || !["currency", "decimal", "integer", "date", "datetime", "lookup", "address"].includes(fieldMeta.type)) && fieldName !== "Name" && {
          renderCell: ({ row }) => {
            const value = row[fieldName];

            // Handle null/undefined values
            if (value === null || value === undefined) {
              return <span>-</span>;
            }

            // Handle object values (like lookup fields that aren't properly typed)
            if (typeof value === 'object') {
              // If it's an object with a Name property, display the Name
              if (value.Name) {
                return <span>{String(value.Name)}</span>;
              }
              // For other objects, display a readable string representation
              if (value._id && value.Name) {
                return <span>{String(value.Name)}</span>;
              }
              // Fallback for complex objects - don't render them directly
              return <span>[Object]</span>;
            }

            // Handle string/number/boolean values
            return <span>{String(value)}</span>;
          },
        }),

        // Summary row rendering for first column
        ...(index === 0 && {
          renderSummaryCell: ({ row }) => {
            return (
              <span style={{ fontWeight: 500 }}>
                Total {row.totalCount} records
              </span>
            );
          }
        }),

        // Summary row rendering for aggregation fields
        ...(selectedAggregationFields.includes(fieldName) && index !== 0 && {
          renderSummaryCell: ({ row }) => {
            const value = row[fieldName];
            if (value !== undefined && value !== null) {
              // Format based on field type
              if (fieldMeta?.type === "currency") {
                const formattedValue = value % 1 !== 0
                  ? Number(value).toFixed(2)
                  : `${value}.00`;
                return (
                  <span style={{ fontWeight: 500, color: '#1890ff' }}>
                    ${formattedValue}
                  </span>
                );
              } else if (fieldMeta?.type === "decimal") {
                const formattedValue = value % 1 !== 0
                  ? Number(value).toFixed(2)
                  : `${value}.00`;
                return (
                  <span style={{ fontWeight: 500, color: '#1890ff' }}>
                    {formattedValue}
                  </span>
                );
              } else {
                // Format the aggregated value appropriately for other types
                const formattedValue = typeof value === 'number' ?
                  value.toLocaleString(undefined, { maximumFractionDigits: 2 }) :
                  String(value);
                return (
                  <span style={{ fontWeight: 500, color: '#1890ff' }}>
                    {formattedValue}
                  </span>
                );
              }
            }
            return <span>-</span>;
          }
        })
      };
    });
  };

  // Calculate summary rows based on aggregation fields from report configuration
  const summaryRows = useMemo(() => {
    if (!reportData || reportData.length === 0) return [];

    const totalCount = reportData.length;

    // Calculate sums specifically for aggregation fields from report config
    const aggregationSums = {};

    if (selectedAggregationFields && selectedAggregationFields.length > 0) {
      selectedAggregationFields.forEach(fieldName => {
        // Only calculate sum if field is in fieldsToDisplay and has numeric data
        if (fieldsToDisplay?.includes(fieldName)) {
          const sum = reportData.reduce((acc, row) => {
            const value = row[fieldName];
            const numValue = Number(value);
            return acc + (isNaN(numValue) ? 0 : numValue);
          }, 0);
          aggregationSums[fieldName] = sum;
        }
      });
    } else {
      // Fallback: Calculate sums for all numeric fields in fieldsToDisplay
      const numericFields = fieldsToDisplay?.filter(fieldName => {
        const firstRow = reportData[0];
        const value = firstRow?.[fieldName];
        return typeof value === 'number' || !isNaN(Number(value));
      }) || [];

      numericFields.forEach(fieldName => {
        const sum = reportData.reduce((acc, row) => acc + (Number(row[fieldName]) || 0), 0);
        aggregationSums[fieldName] = sum;
      });
    }

    return [
      {
        totalCount,
        ...aggregationSums,
      },
    ];
  }, [reportData, fieldsToDisplay, selectedAggregationFields]);

  const columns = hasRunReport && reportData ? generateColumns(reportData) : [];

  // Data transformation functions for different chart types
  const transformDataForBarChart = () => {
    if (!reportData || reportData.length === 0 || !fieldsToDisplay || fieldsToDisplay.length < 2) {
      return [];
    }

    const categoryField = fieldsToDisplay[0]; // Use first field as category
    const valueFields = selectedAggregationFields.length > 0
      ? selectedAggregationFields
      : fieldsToDisplay.slice(1).filter(field => {
        const firstRow = reportData[0];
        const value = firstRow?.[field];
        return typeof value === 'number' || !isNaN(Number(value));
      });

    if (valueFields.length === 0) return [];

    // Group data by category and sum values
    const groupedData = reportData.reduce((acc, row) => {
      const category = row[categoryField];
      const categoryKey = typeof category === 'object' && category !== null
        ? (category.Name || '[Object]')
        : String(category || 'Unknown');

      if (!acc[categoryKey]) {
        acc[categoryKey] = { category: categoryKey };
        valueFields.forEach(field => {
          acc[categoryKey][field] = 0;
        });
      }

      valueFields.forEach(field => {
        const value = Number(row[field]) || 0;
        acc[categoryKey][field] += value;
      });

      return acc;
    }, {});

    return Object.values(groupedData);
  };

  const transformDataForLineChart = () => {
    if (!reportData || reportData.length === 0 || !fieldsToDisplay || fieldsToDisplay.length < 2) {
      return [];
    }

    const xField = fieldsToDisplay[0]; // Use first field as X-axis
    const valueFields = selectedAggregationFields.length > 0
      ? selectedAggregationFields
      : fieldsToDisplay.slice(1).filter(field => {
        const firstRow = reportData[0];
        const value = firstRow?.[field];
        return typeof value === 'number' || !isNaN(Number(value));
      });

    if (valueFields.length === 0) return [];

    return valueFields.map(field => ({
      id: field,
      data: reportData.map(row => ({
        x: typeof row[xField] === 'object' && row[xField] !== null
          ? (row[xField].Name || '[Object]')
          : String(row[xField] || 'Unknown'),
        y: Number(row[field]) || 0
      }))
    }));
  };

  const transformDataForPieChart = () => {
    if (!reportData || reportData.length === 0 || !fieldsToDisplay || fieldsToDisplay.length < 1) {
      console.log("Pie chart: No data or insufficient fields", { reportData: reportData?.length, fieldsToDisplay });
      return [];
    }

    const labelField = fieldsToDisplay[0]; // Use first field as labels

    // Try to find a numeric field for values, or fall back to count
    let valueField = null;
    if (selectedAggregationFields.length > 0) {
      valueField = selectedAggregationFields[0];
    } else if (fieldsToDisplay.length > 1) {
      valueField = fieldsToDisplay.find(field => {
        const firstRow = reportData[0];
        const value = firstRow?.[field];
        return typeof value === 'number' || !isNaN(Number(value));
      });
    }

    console.log("Pie chart transformation:", { labelField, valueField, selectedAggregationFields, fieldsToDisplay });

    // Group data by label and either sum values or count occurrences
    const groupedData = reportData.reduce((acc, row) => {
      const label = row[labelField];
      const labelKey = typeof label === 'object' && label !== null
        ? (label.Name || '[Object]')
        : String(label || 'Unknown');

      if (!acc[labelKey]) {
        acc[labelKey] = 0;
      }

      if (valueField) {
        // Sum the numeric values
        acc[labelKey] += Number(row[valueField]) || 0;
      } else {
        // Count occurrences
        acc[labelKey] += 1;
      }
      return acc;
    }, {});

    console.log("Pie chart grouped data:", groupedData);

    const colors = ['#1890ff', '#52c41a', '#fa8c16', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96'];

    const result = Object.entries(groupedData).map(([label, value], index) => ({
      id: label,
      label: label,
      value: value,
      color: colors[index % colors.length]
    }));

    console.log("Pie chart final data:", result);
    return result;
  };

  // Get chart data based on selected chart type
  const getChartData = () => {
    console.log("Getting chart data for type:", selectedChartType);
    switch (selectedChartType) {
      case 'column':
        return transformDataForBarChart();
      case 'line':
        return transformDataForLineChart();
      case 'pie':
        return transformDataForPieChart();
      default:
        return [];
    }
  };

  const chartData = hasRunReport && reportData ? getChartData() : [];
  console.log("Final chart data:", { selectedChartType, hasRunReport, reportDataLength: reportData?.length, chartData });

  return (
    <>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '32px'
      }}>
        <h3 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#262626',
          margin: '0'
        }}>
          Report Preview
        </h3>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '4px 12px',
          backgroundColor: '#f0f9ff',
          borderRadius: '16px',
          border: '1px solid #e6f4ff'
        }}>
          {selectedChartType === 'table' && <TableOutlined style={{ color: '#1890ff' }} />}
          {selectedChartType === 'column' && <BarChartOutlined style={{ color: '#1890ff' }} />}
          {selectedChartType === 'line' && <LineChartOutlined style={{ color: '#1890ff' }} />}
          {selectedChartType === 'pie' && <PieChartOutlined style={{ color: '#1890ff' }} />}
          <span style={{
            fontSize: '12px',
            fontWeight: '500',
            color: '#1890ff',
            textTransform: 'capitalize'
          }}>
            {selectedChartType === 'column' ? 'Column Chart' :
              selectedChartType === 'line' ? 'Line Chart' :
                selectedChartType === 'pie' ? 'Pie Chart' : 'Table'}
          </span>
        </div>
      </div>

      {loading ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '80px 20px',
          textAlign: 'center',
          backgroundColor: '#fafafa',
          borderRadius: '8px',
          border: '1px solid #e8e8e8'
        }}>
          <LoadingOutlined style={{
            fontSize: '64px',
            color: '#1890ff',
            marginBottom: '24px'
          }} />
          <p style={{
            fontSize: '16px',
            color: '#1890ff',
            margin: '0',
            fontWeight: '500'
          }}>
            Generating your report...
          </p>
        </div>
      ) : hasRunReport ? (
        reportData && reportData.length > 0 ? (
          <>
            <div style={{
              fontSize: '14px',
              color: '#8c8c8c',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>{reportData.length} rows</span>
              <span>•</span>
              <span>Generated successfully</span>
              {selectedChartType !== 'table' && chartData.length > 0 && (
                <>
                  <span>•</span>
                  <span>{chartData.length} data points visualized</span>
                </>
              )}
              {selectedGroupFields.length > 0 && selectedChartType === 'table' && (
                <>
                  <span>•</span>
                  <span>Grouped by: {selectedGroupFields.join(', ')}</span>
                </>
              )}
            </div>

            {/* Chart/Table Rendering based on selectedChartType */}
            {(() => {
              console.log("Rendering chart with type:", selectedChartType, "and chartData:", chartData);

              if (selectedChartType === 'table') {
                return (
                  <div style={{
                    border: '1px solid #e8e8e8',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    backgroundColor: '#ffffff',
                    height: '400px'
                  }}>
                    <style>{`
                      .rdg-light {
                        position: relative;
                        overflow: auto;
                      }

                      .rdg-light:hover::-webkit-scrollbar {
                        width: 6px;
                        height: 6px;
                      }

                      .rdg-light::-webkit-scrollbar {
                        width: 0px;
                        height: 0px;
                      }

                      .rdg-light::-webkit-scrollbar-thumb {
                        background-color: rgba(0, 0, 0, 0.3);
                        border-radius: 3px;
                      }

                      .rdg-light::-webkit-scrollbar-track {
                        background: transparent;
                      }
                    `}</style>
                    <TreeDataGrid
                      columns={columns}
                      rows={reportData || []}
                      style={{
                        height: '400px',
                        fontSize: '14px'
                      }}
                      className="rdg-light"
                      headerRowHeight={40}
                      rowHeight={35}
                      groupBy={selectedGroupFields}
                      expandedGroupIds={expandedGroupIds}
                      onExpandedGroupIdsChange={setExpandedGroupIds}
                      bottomSummaryRows={summaryRows}
                      renderers={{
                        renderGroupCell: ({ column, childRows, groupKey }) => {
                          const fieldName = column.key;
                          const fieldMeta = fields?.find(f => f.name === fieldName);

                          // Helper function to check if value should be displayed as dash
                          const shouldShowDash = (value) => {
                            return value === null ||
                              value === undefined ||
                              value === '' ||
                              value === 'null' ||
                              value === 'undefined' ||
                              value === '-';
                          };

                          // Check if this is the special empty group key or if field value should be dash
                          if (groupKey === '-' || shouldShowDash(groupKey)) {
                            return '-';
                          }

                          // Get the first row to extract the field value (since all rows in a group should have the same grouping value)
                          const firstRow = childRows?.[0];
                          const fieldValue = firstRow?.[fieldName];

                          // Double check the field value as well
                          if (shouldShowDash(fieldValue)) {
                            return <span>-</span>;
                          }

                          if (fieldMeta?.type === "lookup") {
                            const lookupName = fieldValue;
                            const fullObject = firstRow?.[`${fieldName}_object`];
                            const parentObjectName = fieldMeta?.parent_object_name;
                            const redirectUrl = fullObject?._id
                              ? `/record/${parentObjectName}/${fullObject._id}`
                              : null;

                            return (
                              <span
                                style={{
                                  color: redirectUrl ? "#00A1E0" : undefined,
                                  textDecoration: redirectUrl ? "underline" : undefined,
                                  cursor: redirectUrl ? "pointer" : undefined,
                                }}
                                onClick={() => redirectUrl && window.open(redirectUrl, "_blank")}
                              >
                                {lookupName}
                              </span>
                            );
                          }

                          // Handle object values in grouping
                          if (typeof fieldValue === 'object' && fieldValue !== null) {
                            if (fieldValue.Name) {
                              return <span>{String(fieldValue.Name)}</span>;
                            }
                            return <span>[Object]</span>;
                          }

                          // Default for non-lookup group values
                          return <span>{fieldValue}</span>;
                        }
                      }}
                      rowGrouper={(rows, columnKey) => {
                        // Custom grouper to handle null/undefined/empty values
                        const grouped = {};
                        rows.forEach(row => {
                          let key = row[columnKey];

                          // Handle object values for grouping key
                          if (typeof key === 'object' && key !== null) {
                            key = key.Name || '[Object]';
                          }

                          // Normalize null, undefined, empty string, 'null', 'undefined' to a consistent key
                          if (key === null || key === undefined || key === '' || key === 'null' || key === 'undefined') {
                            key = '-'; // Use a special key for empty values
                          }

                          if (!grouped[key]) {
                            grouped[key] = [];
                          }
                          grouped[key].push(row);
                        });
                        return grouped;
                      }}
                    />
                  </div>
                );
              } else if (selectedChartType === 'pie') {
                return (
                  <div style={{
                    border: '1px solid #e8e8e8',
                    borderRadius: '8px',
                    backgroundColor: '#ffffff',
                    height: '400px',
                    padding: '20px'
                  }}>
                    {chartData && chartData.length > 0 ? (
                      <ResponsivePie
                        data={chartData}
                        margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                        innerRadius={0.5}
                        padAngle={0.7}
                        cornerRadius={3}
                        activeOuterRadiusOffset={8}
                        borderWidth={1}
                        borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                        arcLinkLabelsSkipAngle={10}
                        arcLinkLabelsTextColor="#333333"
                        arcLinkLabelsThickness={2}
                        arcLinkLabelsColor={{ from: 'color' }}
                        arcLabelsSkipAngle={10}
                        arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                        legends={[
                          {
                            anchor: 'bottom',
                            direction: 'row',
                            justify: false,
                            translateX: 0,
                            translateY: 56,
                            itemsSpacing: 0,
                            itemWidth: 100,
                            itemHeight: 18,
                            itemTextColor: '#999',
                            itemDirection: 'left-to-right',
                            itemOpacity: 1,
                            symbolSize: 18,
                            symbolShape: 'circle',
                            effects: [
                              {
                                on: 'hover',
                                style: {
                                  itemTextColor: '#000'
                                }
                              }
                            ]
                          }
                        ]}
                        animate={true}
                        motionStiffness={90}
                        motionDamping={15}
                      />
                    ) : (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: '#8c8c8c'
                      }}>
                        <PieChartOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                        <div>No data available for pie chart visualization</div>
                        <div style={{ fontSize: '12px', marginTop: '8px' }}>
                          Chart data length: {chartData?.length || 0}
                        </div>
                      </div>
                    )}
                  </div>
                );
              } else if (selectedChartType === 'column') {
                return (
                  <div style={{
                    border: '1px solid #e8e8e8',
                    borderRadius: '8px',
                    backgroundColor: '#ffffff',
                    height: '400px',
                    padding: '20px'
                  }}>
                    {chartData.length > 0 ? (
                      <ResponsiveBar
                        data={chartData}
                        keys={selectedAggregationFields.length > 0 ? selectedAggregationFields : [fieldsToDisplay?.[1]].filter(Boolean)}
                        indexBy="category"
                        margin={{ top: 50, right: 130, bottom: 50, left: 60 }}
                        padding={0.3}
                        valueScale={{ type: 'linear' }}
                        indexScale={{ type: 'band', round: true }}
                        colors={{ scheme: 'nivo' }}
                        borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                        axisTop={null}
                        axisRight={null}
                        axisBottom={{
                          tickSize: 5,
                          tickPadding: 5,
                          tickRotation: -45,
                          legend: fieldsToDisplay?.[0] || 'Category',
                          legendPosition: 'middle',
                          legendOffset: 40
                        }}
                        axisLeft={{
                          tickSize: 5,
                          tickPadding: 5,
                          tickRotation: 0,
                          legend: 'Value',
                          legendPosition: 'middle',
                          legendOffset: -40
                        }}
                        labelSkipWidth={12}
                        labelSkipHeight={12}
                        labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                        legends={[
                          {
                            dataFrom: 'keys',
                            anchor: 'bottom-right',
                            direction: 'column',
                            justify: false,
                            translateX: 120,
                            translateY: 0,
                            itemsSpacing: 2,
                            itemWidth: 100,
                            itemHeight: 20,
                            itemDirection: 'left-to-right',
                            itemOpacity: 0.85,
                            symbolSize: 20,
                            effects: [
                              {
                                on: 'hover',
                                style: {
                                  itemOpacity: 1
                                }
                              }
                            ]
                          }
                        ]}
                        animate={true}
                        motionStiffness={90}
                        motionDamping={15}
                      />
                    ) : (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: '#8c8c8c'
                      }}>
                        No data available for chart visualization
                      </div>
                    )}
                  </div>
                );
              } else if (selectedChartType === 'line') {
                return (
                  <div style={{
                    border: '1px solid #e8e8e8',
                    borderRadius: '8px',
                    backgroundColor: '#ffffff',
                    height: '400px',
                    padding: '20px'
                  }}>
                    {chartData.length > 0 ? (
                      <ResponsiveLine
                        data={chartData}
                        margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
                        xScale={{ type: 'point' }}
                        yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false, reverse: false }}
                        yFormat=" >-.2f"
                        axisTop={null}
                        axisRight={null}
                        axisBottom={{
                          orient: 'bottom',
                          tickSize: 5,
                          tickPadding: 5,
                          tickRotation: -45,
                          legend: fieldsToDisplay?.[0] || 'Category',
                          legendOffset: 40,
                          legendPosition: 'middle'
                        }}
                        axisLeft={{
                          orient: 'left',
                          tickSize: 5,
                          tickPadding: 5,
                          tickRotation: 0,
                          legend: 'Value',
                          legendOffset: -40,
                          legendPosition: 'middle'
                        }}
                        pointSize={10}
                        pointColor={{ theme: 'background' }}
                        pointBorderWidth={2}
                        pointBorderColor={{ from: 'serieColor' }}
                        pointLabelYOffset={-12}
                        useMesh={true}
                        legends={[
                          {
                            anchor: 'bottom-right',
                            direction: 'column',
                            justify: false,
                            translateX: 100,
                            translateY: 0,
                            itemsSpacing: 0,
                            itemDirection: 'left-to-right',
                            itemWidth: 80,
                            itemHeight: 20,
                            itemOpacity: 0.75,
                            symbolSize: 12,
                            symbolShape: 'circle',
                            symbolBorderColor: 'rgba(0, 0, 0, .5)',
                            effects: [
                              {
                                on: 'hover',
                                style: {
                                  itemBackground: 'rgba(0, 0, 0, .03)',
                                  itemOpacity: 1
                                }
                              }
                            ]
                          }
                        ]}
                        animate={true}
                        motionStiffness={90}
                        motionDamping={15}
                      />
                    ) : (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: '#8c8c8c'
                      }}>
                        No data available for chart visualization
                      </div>
                    )}
                  </div>
                );
              } else {
                return (
                  <div style={{
                    border: '1px solid #e8e8e8',
                    borderRadius: '8px',
                    backgroundColor: '#ffffff',
                    height: '400px',
                    padding: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#8c8c8c'
                  }}>
                    Unknown chart type: {selectedChartType}
                  </div>
                );
              }
            })()}
          </>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '80px 20px',
            textAlign: 'center',
            backgroundColor: '#fafafa',
            borderRadius: '8px',
            border: '1px solid #e8e8e8'
          }}>
            <ExclamationCircleOutlined style={{
              fontSize: '64px',
              color: '#faad14',
              marginBottom: '24px'
            }} />
            <h4 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#262626',
              margin: '0 0 8px 0'
            }}>
              No Data Available
            </h4>
            <p style={{
              fontSize: '16px',
              color: '#8c8c8c',
              margin: '0',
              fontWeight: '400'
            }}>
              No data found for this report configuration
            </p>
          </div>
        )
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '80px 20px',
          textAlign: 'center',
          backgroundColor: '#fafafa',
          borderRadius: '8px',
          border: '1px solid #e8e8e8'
        }}>
          <BarChartOutlined style={{
            fontSize: '64px',
            color: '#d9d9d9',
            marginBottom: '24px'
          }} />
          <p style={{
            fontSize: '16px',
            color: '#8c8c8c',
            margin: '0',
            fontWeight: '500'
          }}>
            Click "Run Report" to see your data
          </p>
        </div>
      )}
    </>
  );
};

const StepsContent = ({
  selectedTables,
  onSelectTable,
  onRemoveTable,
  step,
  selectedFields,
  onRemoveField,
  onAddField,
  filters,
  onAddFilter,
  onRemoveFilter,
  onFilterChange,
  selectedChartType,
  onSelectChartType,
  hasRunReport,
  reportData,
  setFilters,
  logic,
  setLogic,
  fieldsToDisplay,
  onFieldsToDisplayChange,
  loading,
  fields,
  reportGroupFields,
  reportAggregationFields,
  setAggregrationType,
  aggregrationType,
  isDetail,
  setIsDetail,
  is_collapsed,
  setIs_collapsed,
  sortBy,
  setSortBy,
  selectedFilters

 }) => {
  // Debug logging for StepsContent
  console.log("StepsContent props:", { step, selectedChartType });

  switch (step) {
    case 1:
      return (
        <Step1Content
          selectedTables={selectedTables}
          onSelectTable={onSelectTable}
          onRemoveTable={onRemoveTable}
        />
      );
    case 2:
      return (
        <Step2Content
          selectedFields={selectedFields}
          onRemoveField={onRemoveField}
          onAddField={onAddField}
          selectedTables={selectedTables}
          fieldsToDisplay={fieldsToDisplay}
          onFieldsToDisplayChange={onFieldsToDisplayChange}
          setAggregrationType={setAggregrationType}
          aggregrationType={aggregrationType}
          isDetail={isDetail}
          setIsDetail={setIsDetail}
          is_collapsed={is_collapsed}
          setIs_collapsed={setIs_collapsed}
          sortBy={sortBy}
          setSortBy={setSortBy}
        />
      );
    case 3:
      return (
        <Step3Content
          filters={filters}
          setFilters={setFilters}
          selectedTables={selectedTables}
          logic={logic}
          setLogic={setLogic}
          selectedFilters={selectedFilters}
        />
      );
    case 4:
      return (
        <Step4Content
          selectedChartType={selectedChartType}
          onSelectChartType={onSelectChartType}
        />
      );
    case 5:
      return (
        <Step5Content
          hasRunReport={hasRunReport}
          reportData={reportData}
          loading={loading}
          fieldsToDisplay={fieldsToDisplay}
          selectedTables={selectedTables}
          fields={fields}
          selectedFields={selectedFields}
          reportGroupFields={reportGroupFields}
          reportAggregationFields={reportAggregationFields}
          selectedChartType={selectedChartType}
        />
      );
    default:
      return null;
  }
}

export default StepsContent